// Frame derivations — pure view facts projected from frame factors.
//
// Split out of `app/frame.ts` (C1 hotspot decomposition). Each is a pure
// projection the shell (AppShell) only renders; none writes frame state.

import type {
    DockState,
    OpenSecondary,
    WorkspaceSurface,
} from "../../components/AppShell/AppShell.js";
import type { ActiveTab } from "../../components/Navigation/types.js";

// Whether the right dock (the SecondaryPane + its ribbon — Review / Quality /
// Files) belongs on the CURRENT centre. Review + Quality are material-scoped
// (they read a material being authored); Files (dogfood F4) is conversation-
// scoped. So the dock applies on a material editor OR a conversation — the two
// centres that have a dock panel to offer. Pure — the host derives
// `dockApplies` from the active tab and gates the dock mount on this.
export const showsSecondaryDock = (dockApplies: boolean): boolean =>
    dockApplies;

// The right dock as ONE derived value — the single fact the panel body, the
// ribbon, and the resize handle all project from (ui-robustness.md §3.1). This
// COLLAPSES the two conditionals that caused the stuck-handle bug: the body was
// gated on `showsSecondaryDock` while the handle read the raw `openSecondary`,
// so a non-material tab left the handle floating without its panel. Now both
// come from here:
//   - no dock panel applies            → `gone`      (nothing renders)
//   - applies, no slot summoned (Focus)→ `collapsed` (ribbon + collapsed body)
//   - applies + a summoned slot        → `open`      (ribbon + panel + handle)
// Pure — the derivation of a view fact from frame factors; the shell (AppShell)
// only renders it. Mirrors `showsSecondaryDock` and reuses it for the gate so
// the two can never drift.
export const dockState = (
    dockApplies: boolean,
    openSecondary: OpenSecondary,
): DockState =>
    !showsSecondaryDock(dockApplies)
        ? { type: "gone" }
        : openSecondary === null
          ? { type: "collapsed" }
          : { type: "open", slot: openSecondary };

// The centre's surface register, DERIVED from the focused tab + whether an
// active conversation is EMPTY — the ONE fact the ambient-vs-paper background
// swap reads (the workspace-landing sketch's single idea; the type + the CSS
// live in AppShell). An EMPTY conversation tab is the landing (the
// WorkspaceLanding hero composer, sharing the chrome's ambient ground); any
// other focused tab — including a non-empty conversation — is a work surface
// lifted to paper. `activeConversationEmpty` is the derived nascency the shell
// already computes from the session's transcript length
// (hero-is-initial-conversation-state §1.1); it is ignored for non-conversation
// tabs. Pure — a projection of frame factors, mirroring
// `dockState`/`showsSecondaryDock`; the host derives the active tab off the
// (focused-leaf) pane tree and the shell only renders it.
export const workspaceSurface = (
    active: ActiveTab,
    activeConversationEmpty: boolean,
): WorkspaceSurface =>
    active.type === "conversation" && activeConversationEmpty
        ? { type: "landing" }
        : { type: "paper" };
